import { action, onConnect } from '@reatom/framework'

import { AsyncStateN, Iterable, pipe, Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { TxSerialized } from '@root/entities/transaction'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { ApiError, apiRequest } from '@root/shared/api'
import { TradeErrorCode } from '@root/shared/api/api-error-code'
import { reatomRequest } from '@root/shared/lib'

import { InsufficientBalanceLightError } from '../../validation-errors'

import type { TypeHelper } from '@blade/std-lib'
import type {
  TradeCheckAllowanceListParams,
  TradePrepareApproveTokensInstructionsCreatePayload,
  TradeSendApproveTokensTransactionsCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'
import type { WalletModel } from '@root/entities/wallet/atoms'

export const checkAllowanceRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OmitKey<
      TradeCheckAllowanceListParams,
      'network' | 'tokens'
    > & {
      tokens: Array<CurrencyAddress>
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeCheckAllowanceList, [
        {
          ...req,
          network: Network.toDto(network),
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'checkAllowanceRequestAction',
)

export const prepareApproveTokensInstructionsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OmitKey<
      TradePrepareApproveTokensInstructionsCreatePayload,
      'network' | 'signedApproves'
    > & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradePrepareApproveTokensInstructionsCreate,
        [
          {
            ...req,
            network: Network.toDto(network),
          },
        ],
      ),
    )

    return pipe(
      result,
      Result.map((values) =>
        values.map((value) => ({
          ...value,
          serializedApprove: TxSerialized(value.serializedApprove),
        })),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'prepareApproveTokensInstructionsRequestAction',
)

export const sendApproveTokensInstructionsRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OmitKey<
      TradeSendApproveTokensTransactionsCreatePayload,
      'network'
    > & {
      network: Network.Network
    },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradeSendApproveTokensTransactionsCreate,
        [
          {
            ...req,
            network: Network.toDto(network),
          },
        ],
      ),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'sendApproveTokensInstructionsRequestAction',
)

export const reatomCheckEvmAllowance = ({
  currencies,
  wallet,
}: {
  currencies: Array<CurrencyAddress>
  wallet: WalletModel
}) => {
  const allowanceStateAtom = reatomRequest({
    execAction: checkAllowanceRequestAction,
    prepare: (ctx) => {
      const network = ctx.get(selectedNetworkAtom)
      return {
        network,
        tokens: currencies,
        walletAddress: wallet.address,
      }
    },
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'checkEvmAllowance',
  })

  onConnect(allowanceStateAtom, (ctx) => {
    const allowanceState = ctx.get(allowanceStateAtom)
    if (AsyncStateN.isSuccess(allowanceState)) {
      return
    }
    allowanceStateAtom.execute(ctx)
  })

  return allowanceStateAtom
}

export type ApproveEvmAllowanceError =
  | Error.UnknownError
  | InsufficientBalanceLightError

export const approveEvmAllowanceAction = action(
  async (
    ctx,
    {
      currencies,
      wallet,
    }: {
      currencies: Array<string>
      wallet: WalletModel
    },
  ): Promise<Result.Result<undefined, ApproveEvmAllowanceError>> => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const network = ctx.get(selectedNetworkAtom)

    const instructionsResult = await ctx.schedule(() =>
      prepareApproveTokensInstructionsRequestAction(ctx, {
        network: wallet.network,
        tokens: currencies,
        walletAddress: wallet.address,
        walletId: wallet.id,
      }),
    )

    if (Result.isFailure(instructionsResult)) {
      return Result.fail(instructionsResult.failure)
    }

    const instructions = instructionsResult.success

    const signedInstructions: Array<{
      signedApprove: string
      token: string
    }> = []
    for (const instruction of instructions) {
      const signedInstruction = await ctx.schedule(() =>
        secureStorageService.signTransaction({
          network,
          password: undefined,
          walletId: wallet.id,
          tx: instruction.serializedApprove,
          operation: 'swap',
        }),
      )
      if (Result.isFailure(signedInstruction)) {
        return Result.fail(
          Error.UnknownError.fromUnknown(signedInstruction.failure),
        )
      }

      signedInstructions.push({
        signedApprove: signedInstruction.success.txSigned,
        token: instruction.token,
      })
    }

    const sendResult = await ctx.schedule(() =>
      sendApproveTokensInstructionsRequestAction(ctx, {
        network: wallet.network,
        tokens: currencies,
        walletAddress: wallet.address,
        walletId: wallet.id,
        signedApproves: signedInstructions,
      }),
    )

    if (Result.isFailure(sendResult)) {
      return Result.fail(sendResult.failure)
    }

    const errors = pipe(
      sendResult.success,
      Iterable.map((it) => it.error),
      Iterable.filter((it) => it !== undefined),
      Iterable.toArray,
    )

    if (errors.length === 0) {
      return Result.succeed(undefined)
    }

    if (errors.includes(TradeErrorCode.ApproveInsufficientFunds)) {
      return Result.fail(new InsufficientBalanceLightError())
    }

    return Result.fail(Error.UnknownError.fromUnknown(errors))
  },
  'approveEvmAllowanceAction',
)
